<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import { useVuelidate } from '@vuelidate/core';
import { minValue } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsAccordion from 'dashboard/components-next/Settings/SettingsAccordion.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import SearchInput from 'dashboard/components-next/input/SearchInput.vue';
import assignmentPoliciesAPI from 'dashboard/api/assignmentPolicies';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { isEnterprise } = useConfig();

const selectedAgentIds = ref([]);
const agentEligibility = ref({});
const addSearch = ref('');
const showAddDropdown = ref(false);
const isAgentListUpdating = ref(false);
// Snapshot of the agent membership/eligibility at load/after save, for the dirty state.
const savedAgentsState = ref('');
const enableAutoAssignment = ref(false);
const maxAssignmentLimit = ref(null);
const defaultTeamId = ref(null);
const isUpdatingDefaultTeam = ref(false);
const assignmentPolicy = ref(null);
const isLoadingPolicy = ref(false);
const isDeletingPolicy = ref(false);
const showDeleteConfirmModal = ref(false);
const availablePolicies = ref([]);
const isLoadingPolicies = ref(false);
const showPolicyDropdown = ref(false);
const isLinkingPolicy = ref(false);

const agentList = computed(() => store.getters['agents/getAgents']);

// Agents currently in the inbox (in order they were added)
const memberAgents = computed(() =>
  selectedAgentIds.value
    .map(id => agentList.value.find(a => a.id === id))
    .filter(Boolean)
);

// Agents NOT yet in inbox, filtered by search
const addableAgents = computed(() => {
  const q = addSearch.value.toLowerCase();
  return agentList.value.filter(
    a =>
      !selectedAgentIds.value.includes(a.id) &&
      (!q || a.name.toLowerCase().includes(q))
  );
});

// Teams as a shortcut: picking one adds all its members at once (agents can still
// be removed individually afterwards).
const teamsList = computed(() => store.getters['teams/getTeams'] || []);
const addableTeams = computed(() => {
  const q = addSearch.value.toLowerCase();
  return teamsList.value.filter(
    team => !q || team.name.toLowerCase().includes(q)
  );
});

// The closing flow follows the agent's team, so adding someone to a caixa never
// changes which flow they use — no conflict to warn about here.
const addAgent = agent => {
  selectedAgentIds.value = [...selectedAgentIds.value, agent.id];
  agentEligibility.value = { ...agentEligibility.value, [agent.id]: true };
  addSearch.value = '';
  showAddDropdown.value = false;
};

const addTeam = async team => {
  try {
    const TeamsAPI = (await import('dashboard/api/teams')).default;
    const { data } = await TeamsAPI.getAgents({ teamId: team.id });
    const memberIds = (data || []).map(member => member.id);
    const newIds = memberIds.filter(
      id =>
        !selectedAgentIds.value.includes(id) &&
        agentList.value.some(agent => agent.id === id)
    );
    if (newIds.length) {
      selectedAgentIds.value = [...selectedAgentIds.value, ...newIds];
      const eligibility = { ...agentEligibility.value };
      newIds.forEach(id => {
        eligibility[id] = true;
      });
      agentEligibility.value = eligibility;
    }
    // Also link this caixa to the team (TeamInbox), so team membership and caixa
    // access stay mirrored — the closing flow itself lives on the caixa.
    const linkedInboxIds = team.inbox_ids || [];
    let linkedNow = false;
    if (!linkedInboxIds.includes(props.inbox.id)) {
      await TeamsAPI.updateInboxes({
        teamId: team.id,
        inboxIds: [...linkedInboxIds, props.inbox.id],
      });
      store.dispatch('teams/get', { cache: false });
      linkedNow = true;
    }
    const agentsPart = newIds.length
      ? `${newIds.length} agente(s) do time "${team.name}" adicionado(s)`
      : `Todos os agentes do time "${team.name}" já estão na caixa`;
    useAlert(linkedNow ? `${agentsPart}. Caixa vinculada ao time` : agentsPart);
  } catch {
    useAlert('Não foi possível adicionar o time à caixa');
  }
  addSearch.value = '';
  showAddDropdown.value = false;
};

const removeAgent = agentId => {
  selectedAgentIds.value = selectedAgentIds.value.filter(id => id !== agentId);
  const updated = { ...agentEligibility.value };
  delete updated[agentId];
  agentEligibility.value = updated;
};

const toggleEligibility = agentId => {
  agentEligibility.value = {
    ...agentEligibility.value,
    [agentId]: !agentEligibility.value[agentId],
  };
};

const closeAddDropdown = () => {
  showAddDropdown.value = false;
};

// Stable representation of the agent list + eligibility, so the Update button only
// enables when something actually changed (and greys out again after saving).
const serializeAgents = () => {
  const ids = [...selectedAgentIds.value].sort((a, b) => a - b);
  const elig = ids.map(id => [id, agentEligibility.value[id] !== false]);
  return JSON.stringify({ ids, elig });
};
const isAgentsDirty = computed(
  () => savedAgentsState.value !== serializeAgents()
);

const isFeatureEnabled = feature => {
  const accountId = Number(route.params.accountId);
  return store.getters['accounts/isFeatureEnabledonAccount'](
    accountId,
    feature
  );
};

const hasAdvancedAssignment = computed(() => {
  return isFeatureEnabled('advanced_assignment');
});

const hasAssignmentV2 = computed(() => {
  return isFeatureEnabled('assignment_v2');
});

const showAdvancedAssignmentUI = computed(() => {
  return hasAdvancedAssignment.value && hasAssignmentV2.value;
});

const assignmentOrderLabel = computed(() => {
  if (!assignmentPolicy.value) return '';
  const priority = assignmentPolicy.value.conversation_priority;
  if (priority === 'earliest_created') {
    return t('INBOX_MGMT.ASSIGNMENT.PRIORITY.EARLIEST_CREATED');
  }
  if (priority === 'longest_waiting') {
    return t('INBOX_MGMT.ASSIGNMENT.PRIORITY.LONGEST_WAITING');
  }
  return priority;
});

const assignmentMethodLabel = computed(() => {
  if (!assignmentPolicy.value) return '';
  const order = assignmentPolicy.value.assignment_order;
  if (order === 'round_robin') {
    return t('INBOX_MGMT.ASSIGNMENT.METHOD.ROUND_ROBIN');
  }
  if (order === 'balanced') {
    return t('INBOX_MGMT.ASSIGNMENT.METHOD.BALANCED');
  }
  return order;
});

// Vuelidate validation rules
const rules = {
  maxAssignmentLimit: {
    minValue: minValue(1),
  },
};

const v$ = useVuelidate(rules, { maxAssignmentLimit });

const assignmentHeader = computed(() =>
  hasAssignmentV2.value
    ? t('INBOX_MGMT.ASSIGNMENT.ENABLE_AUTO_ASSIGNMENT')
    : t('INBOX_MGMT.SETTINGS_POPUP.AUTO_ASSIGNMENT')
);

const assignmentDescription = computed(() =>
  hasAssignmentV2.value
    ? t('INBOX_MGMT.ASSIGNMENT.DESCRIPTION')
    : t('INBOX_MGMT.SETTINGS_POPUP.AUTO_ASSIGNMENT_SUB_TEXT')
);

const maxAssignmentLimitErrors = computed(() => {
  if (v$.value.maxAssignmentLimit.$error) {
    return t('INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT_RANGE_ERROR');
  }
  return '';
});

const fetchAttachedAgents = async () => {
  try {
    const response = await store.dispatch('inboxMembers/get', {
      inboxId: props.inbox.id,
    });
    const {
      data: { payload: inboxMembers },
    } = response;
    selectedAgentIds.value = inboxMembers.map(m => m.id);
    const map = {};
    inboxMembers.forEach(m => {
      map[m.id] = m.eligible_for_assignment !== false;
    });
    agentEligibility.value = map;
    savedAgentsState.value = serializeAgents();
  } catch (error) {
    //  Handle error
  }
};

const fetchAssignmentPolicy = async () => {
  if (!props.inbox.id) return;

  isLoadingPolicy.value = true;
  try {
    const response = await assignmentPoliciesAPI.getInboxPolicy(props.inbox.id);
    assignmentPolicy.value = response.data;
  } catch (error) {
    // No policy attached, which is fine
    assignmentPolicy.value = null;
  } finally {
    isLoadingPolicy.value = false;
  }
};

const fetchAvailablePolicies = async () => {
  isLoadingPolicies.value = true;
  try {
    const response = await assignmentPoliciesAPI.get();
    availablePolicies.value = response.data;
  } catch (error) {
    availablePolicies.value = [];
  } finally {
    isLoadingPolicies.value = false;
  }
};

const linkPolicyToInbox = async policy => {
  isLinkingPolicy.value = true;
  try {
    await assignmentPoliciesAPI.setInboxPolicy(props.inbox.id, policy.id);
    assignmentPolicy.value = policy;
    showPolicyDropdown.value = false;
    useAlert(t('INBOX_MGMT.ASSIGNMENT.LINK_SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.ASSIGNMENT.LINK_ERROR'));
  } finally {
    isLinkingPolicy.value = false;
  }
};

const navigateToAssignmentPolicies = () => {
  const accountId = route.params.accountId;
  router.push({
    name: 'agent_assignment_policy_index',
    params: { accountId },
  });
};

const policyMenuItems = computed(() => {
  const items = availablePolicies.value.map(policy => ({
    action: 'select_policy',
    value: policy.id,
    label: policy.name,
    icon: 'i-lucide-zap',
    policy,
  }));

  items.push({
    action: 'view_all',
    value: 'view_all',
    label: t('INBOX_MGMT.ASSIGNMENT.VIEW_ALL_POLICIES'),
    icon: 'i-lucide-arrow-right',
  });

  return items;
});

const handlePolicyMenuAction = ({ action, policy }) => {
  if (action === 'select_policy' && policy) {
    linkPolicyToInbox(policy);
  } else if (action === 'view_all') {
    navigateToAssignmentPolicies();
  }
  showPolicyDropdown.value = false;
};

const togglePolicyDropdown = () => {
  if (!showPolicyDropdown.value && availablePolicies.value.length === 0) {
    fetchAvailablePolicies();
  }
  showPolicyDropdown.value = !showPolicyDropdown.value;
};

const closePolicyDropdown = () => {
  showPolicyDropdown.value = false;
};

const updateDefaultTeam = async teamId => {
  isUpdatingDefaultTeam.value = true;
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      default_team_id: teamId,
    });
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isUpdatingDefaultTeam.value = false;
  }
};

const handleToggleAutoAssignment = async val => {
  enableAutoAssignment.value = val;
  try {
    const payload = {
      id: props.inbox.id,
      formData: false,
      enable_auto_assignment: val,
    };
    await store.dispatch('inboxes/updateInbox', payload);
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

async function updateAgents() {
  isAgentListUpdating.value = true;
  try {
    const members = selectedAgentIds.value.map(id => ({
      user_id: id,
      eligible_for_assignment: agentEligibility.value[id] !== false,
    }));
    await store.dispatch('inboxMembers/createWithEligibility', {
      inboxId: props.inbox.id,
      members,
    });
    savedAgentsState.value = serializeAgents();
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
  isAgentListUpdating.value = false;
}

const updateInbox = async () => {
  try {
    const payload = {
      id: props.inbox.id,
      formData: false,
      enable_auto_assignment: enableAutoAssignment.value,
      auto_assignment_config: {
        max_assignment_limit: maxAssignmentLimit.value,
      },
    };
    await store.dispatch('inboxes/updateInbox', payload);
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const navigateToCreatePolicy = () => {
  const accountId = route.params.accountId;
  router.push({
    name: 'agent_assignment_policy_create',
    params: { accountId },
    query: { inboxId: props.inbox.id },
  });
};

const navigateToAssignmentPolicyEdit = () => {
  if (!assignmentPolicy.value?.id) return;
  const accountId = route.params.accountId;
  router.push({
    name: 'agent_assignment_policy_edit',
    params: { accountId, id: assignmentPolicy.value.id },
  });
};

const navigateToBilling = () => {
  const accountId = route.params.accountId;
  router.push({
    name: 'billing_settings_index',
    params: { accountId },
  });
};

const confirmDeletePolicy = () => {
  showDeleteConfirmModal.value = true;
};

const cancelDeletePolicy = () => {
  showDeleteConfirmModal.value = false;
};

const deleteAssignmentPolicy = async () => {
  if (isDeletingPolicy.value) return;
  isDeletingPolicy.value = true;
  try {
    await assignmentPoliciesAPI.removeInboxPolicy(props.inbox.id);
    assignmentPolicy.value = null;
    showDeleteConfirmModal.value = false;
    useAlert(t('INBOX_MGMT.ASSIGNMENT_POLICY.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.ASSIGNMENT_POLICY.DELETE_ERROR'));
  } finally {
    isDeletingPolicy.value = false;
  }
};

const setDefaults = () => {
  enableAutoAssignment.value = props.inbox.enable_auto_assignment;
  maxAssignmentLimit.value =
    props.inbox.auto_assignment_config?.max_assignment_limit || null;
  defaultTeamId.value = props.inbox.default_team_id || null;
  fetchAttachedAgents();
  if (showAdvancedAssignmentUI.value) {
    fetchAssignmentPolicy();
    fetchAvailablePolicies();
  }
};

// Watch only inbox.id to avoid unnecessary refetches when other properties change
watch(() => props.inbox.id, setDefaults);

onMounted(() => {
  setDefaults();
  // Never dispatch 'inboxes/get' here: Settings.vue unmounts this tab while inboxes
  // are fetching, so refetching from onMounted causes an infinite remount loop.
  store.dispatch('teams/get', { cache: false });
});
</script>

<template>
  <div>
    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_AGENTS')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.INBOX_AGENTS_SUB_TEXT')"
      class="[&>div]:!items-start"
    >
      <div class="flex flex-col gap-2">
        <!-- Add agent row -->
        <div v-on-click-outside="closeAddDropdown" class="relative">
          <div class="flex gap-2">
            <SearchInput
              v-model="addSearch"
              class="flex-1"
              placeholder="Buscar e adicionar agente..."
              @focus="showAddDropdown = true"
            />
          </div>

          <!-- Dropdown: whole teams as shortcuts, then individual agents -->
          <div
            v-if="
              showAddDropdown && (addableAgents.length || addableTeams.length)
            "
            class="absolute z-10 mt-1 w-full rounded-lg border border-n-weak bg-n-solid-1 shadow-lg max-h-56 overflow-y-auto"
          >
            <template v-if="addableTeams.length">
              <p
                class="px-3 pt-2 pb-1 text-xs font-medium uppercase text-n-slate-10"
              >
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.ADD_TEAM_SHORTCUT_LABEL') }}
              </p>
              <button
                v-for="team in addableTeams"
                :key="`team-${team.id}`"
                type="button"
                class="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-slate-2 text-left"
                @click="addTeam(team)"
              >
                <span class="i-lucide-users text-n-slate-9 shrink-0" />
                {{ team.name }}
              </button>
              <div class="border-t border-n-weak my-1" />
            </template>
            <p
              v-if="addableAgents.length"
              class="px-3 pt-2 pb-1 text-xs font-medium uppercase text-n-slate-10"
            >
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.AGENTS_LABEL') }}
            </p>
            <button
              v-for="agent in addableAgents"
              :key="agent.id"
              type="button"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-slate-2 text-left"
              @click="addAgent(agent)"
            >
              <span class="i-lucide-user-plus text-n-slate-9 shrink-0" />
              {{ agent.name }}
            </button>
          </div>
          <div
            v-else-if="showAddDropdown && addSearch"
            class="absolute z-10 mt-1 w-full rounded-lg border border-n-weak bg-n-solid-1 shadow-lg px-3 py-3 text-sm text-n-slate-10"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.NO_AGENT_OR_TEAM_FOUND') }}
          </div>
        </div>

        <!-- Members table — only current inbox members -->
        <div class="rounded-lg border border-n-weak overflow-hidden">
          <div
            class="flex items-center px-3 py-2 bg-n-slate-2 border-b border-n-weak"
          >
            <span class="text-xs font-medium text-n-slate-11 flex-1">{{
              $t('INBOX_MGMT.SETTINGS_POPUP.TABLE_HEADER_AGENT')
            }}</span>
            <span class="text-xs font-medium text-n-slate-11 w-36 text-center">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.TABLE_HEADER_ELIGIBLE') }}
            </span>
            <span class="w-16" />
          </div>
          <div class="max-h-64 overflow-y-auto">
            <div
              v-for="agent in memberAgents"
              :key="agent.id"
              class="flex items-center px-3 py-2.5 border-b border-n-weak/50 last:border-0 hover:bg-n-slate-1 transition-colors"
            >
              <span class="text-sm text-n-slate-12 flex-1">{{
                agent.name
              }}</span>

              <!-- Eligibility toggle -->
              <div class="w-36 flex justify-center">
                <Switch
                  :model-value="agentEligibility[agent.id]"
                  @change="toggleEligibility(agent.id)"
                />
              </div>

              <!-- Remove button -->
              <button
                type="button"
                class="w-16 flex items-center justify-center gap-1 text-xs text-n-slate-11 hover:text-n-ruby-11 transition-colors"
                :title="$t('INBOX_MGMT.SETTINGS_POPUP.REMOVE_AGENT_TITLE')"
                @click="removeAgent(agent.id)"
              >
                <span class="i-lucide-trash-2 size-3.5 shrink-0" />
                {{ $t('INBOX_MGMT.SETTINGS_POPUP.REMOVE') }}
              </button>
            </div>

            <div
              v-if="memberAgents.length === 0"
              class="px-3 py-5 text-sm text-n-slate-10 text-center"
            >
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.NO_AGENTS_ADDED') }}
            </div>
          </div>
        </div>

        <p class="text-xs text-n-slate-10">
          {{ memberAgents.length }}
          {{
            memberAgents.length === 1
              ? $t('INBOX_MGMT.SETTINGS_POPUP.AGENT_COUNT_SINGULAR')
              : $t('INBOX_MGMT.SETTINGS_POPUP.AGENT_COUNT_PLURAL')
          }}
        </p>
      </div>

      <template #extra>
        <div class="grid grid-cols-1 lg:grid-cols-8">
          <div class="col-span-1 lg:col-span-2" />
          <div class="col-span-1 lg:col-span-6 mt-4 justify-self-end">
            <NextButton
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
              :is-loading="isAgentListUpdating"
              :disabled="!isAgentsDirty"
              @click="updateAgents"
            />
          </div>
        </div>
      </template>
    </SettingsFieldSection>

    <SettingsFieldSection
      :label="$t('INBOX_MGMT.SETTINGS_POPUP.DEFAULT_TEAM')"
      :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.DEFAULT_TEAM_SUB_TEXT')"
      class="mt-6"
    >
      <div class="relative w-full max-w-sm">
        <select
          v-model="defaultTeamId"
          :disabled="isUpdatingDefaultTeam"
          class="reset-base w-full appearance-none rounded-lg border border-n-weak bg-n-solid-1 py-2 pl-3 pr-8 text-sm text-n-slate-12"
          @change="updateDefaultTeam(defaultTeamId)"
        >
          <option :value="null">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.DEFAULT_TEAM_NONE_OPTION') }}
          </option>
          <option v-for="team in teamsList" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <span
          class="i-lucide-chevron-down pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
        />
      </div>
    </SettingsFieldSection>

    <SettingsAccordion
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.AGENT_ASSIGNMENT')"
      class="mt-6"
    >
      <SettingsToggleSection
        v-model="enableAutoAssignment"
        compact
        :header="assignmentHeader"
        :description="assignmentDescription"
        @update:model-value="handleToggleAutoAssignment"
      >
        <template
          v-if="enableAutoAssignment && (isEnterprise || hasAssignmentV2)"
          #editor
        >
          <!-- assignment_v2 UI -->
          <template v-if="hasAssignmentV2">
            <!-- Policy Card - When policy is attached -->
            <div
              v-if="showAdvancedAssignmentUI && assignmentPolicy"
              class="ltr:pr-0 rtl:pl-0 ltr:pl-4 rtl:pr-4 py-4"
            >
              <div class="flex items-start gap-4">
                <div
                  class="flex-shrink-0 size-10 rounded-xl bg-n-slate-3 flex items-center justify-center"
                >
                  <span class="i-lucide-zap text-xl text-n-slate-11" />
                </div>
                <div class="flex-grow">
                  <div
                    class="flex items-start justify-between gap-4 mb-4 ltr:pr-4 rtl:pl-4"
                  >
                    <div class="flex flex-col items-start">
                      <span class="text-heading-3 text-n-slate-12 mb-1">
                        {{ assignmentPolicy.name }}
                      </span>
                      <p class="text-body-main text-n-slate-11">
                        {{ $t('INBOX_MGMT.ASSIGNMENT.POLICY_LABEL') }}
                      </p>
                    </div>
                    <NextButton
                      icon="i-lucide-trash-2"
                      ghost
                      ruby
                      sm
                      @click="confirmDeletePolicy"
                    />
                  </div>

                  <ul class="space-y-2 mb-6">
                    <li class="flex items-center gap-2">
                      <span
                        class="w-1.5 h-1.5 rounded-full bg-n-slate-11 flex-shrink-0"
                      />
                      <span class="text-body-main text-n-slate-12">
                        {{ assignmentOrderLabel }}
                      </span>
                    </li>
                    <li class="flex items-center gap-2">
                      <span
                        class="w-1.5 h-1.5 rounded-full bg-n-slate-11 flex-shrink-0"
                      />
                      <span class="text-body-main text-n-slate-12">
                        {{ assignmentMethodLabel }}
                      </span>
                    </li>
                  </ul>

                  <div class="w-full h-px my-4 bg-n-weak" />

                  <NextButton
                    :label="$t('INBOX_MGMT.ASSIGNMENT.CUSTOMIZE_POLICY')"
                    icon="i-lucide-arrow-right"
                    trailing-icon
                    link
                    class="mb-2"
                    @click="navigateToAssignmentPolicyEdit"
                  />
                </div>
              </div>
            </div>

            <!-- Default Policy - When no custom policy attached but feature enabled -->
            <div
              v-else-if="
                showAdvancedAssignmentUI &&
                !assignmentPolicy &&
                !isLoadingPolicy
              "
            >
              <!-- Default Policy Header -->
              <div class="p-4">
                <div class="flex items-start gap-4">
                  <div
                    class="flex-shrink-0 size-10 rounded-xl bg-n-slate-3 dark:bg-n-slate-4 flex items-center justify-center"
                  >
                    <i class="i-lucide-zap text-xl text-n-slate-11" />
                  </div>
                  <div class="flex-grow">
                    <h4 class="text-heading-3 text-n-slate-12 mb-0.5">
                      {{ $t('INBOX_MGMT.ASSIGNMENT.DEFAULT_POLICY_LINKED') }}
                    </h4>
                    <p class="text-body-main text-n-slate-11">
                      {{
                        $t('INBOX_MGMT.ASSIGNMENT.DEFAULT_POLICY_DESCRIPTION')
                      }}
                    </p>
                  </div>
                </div>

                <!-- Action Buttons -->
                <div class="mt-5 flex items-center gap-3">
                  <div
                    v-if="!isLoadingPolicies && availablePolicies.length > 0"
                    v-on-click-outside="closePolicyDropdown"
                    class="relative"
                  >
                    <NextButton
                      icon="i-lucide-link"
                      sm
                      @click="togglePolicyDropdown"
                    >
                      {{ $t('INBOX_MGMT.ASSIGNMENT.LINK_EXISTING_POLICY') }}
                      <Icon
                        icon="i-lucide-chevron-down"
                        class="transition-transform flex-shrink-0"
                        :class="{ 'rotate-180': showPolicyDropdown }"
                      />
                    </NextButton>

                    <DropdownMenu
                      v-if="showPolicyDropdown"
                      class="top-full ltr:left-0 rtl:right-0 mt-2 max-w-64 max-h-72 overflow-y-auto"
                      :menu-items="policyMenuItems"
                      :is-searching="isLoadingPolicies"
                      @action="handlePolicyMenuAction"
                    />
                  </div>

                  <NextButton
                    icon="i-lucide-plus"
                    :label="$t('INBOX_MGMT.ASSIGNMENT.CREATE_NEW_POLICY')"
                    slate
                    faded
                    sm
                    @click="navigateToCreatePolicy"
                  />
                </div>
              </div>

              <!-- Default Rules Info -->
              <div
                class="px-4 py-4 border-t border-n-weak bg-n-slate-2 rounded-b-xl"
              >
                <div class="flex items-start gap-3">
                  <Icon icon="i-lucide-info" class="mt-0.5 text-n-slate-11" />
                  <div>
                    <p class="text-body-main text-n-slate-11 mb-2">
                      {{ $t('INBOX_MGMT.ASSIGNMENT.CURRENT_BEHAVIOR') }}
                    </p>
                    <ul class="space-y-1">
                      <li class="flex items-center gap-2">
                        <span
                          class="w-1 h-1 rounded-full bg-n-slate-10 flex-shrink-0"
                        />
                        <span class="text-body-main text-n-slate-11">
                          {{ $t('INBOX_MGMT.ASSIGNMENT.DEFAULT_RULE_1') }}
                        </span>
                      </li>
                      <li class="flex items-center gap-2">
                        <span
                          class="w-1 h-1 rounded-full bg-n-slate-10 flex-shrink-0"
                        />
                        <span class="text-body-main text-n-slate-11">
                          {{ $t('INBOX_MGMT.ASSIGNMENT.DEFAULT_RULE_2') }}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- Default Rules Card - Feature not enabled (no advanced_assignment) -->
            <div
              v-else-if="!showAdvancedAssignmentUI"
              class="ltr:pr-0 rtl:pl-0 ltr:pl-4 rtl:pr-4 py-4"
            >
              <div class="flex items-start gap-4">
                <div
                  class="flex-shrink-0 size-10 rounded-xl bg-n-slate-3 dark:bg-n-slate-4 flex items-center justify-center"
                >
                  <Icon icon="i-lucide-zap" class="text-xl text-n-slate-11" />
                </div>
                <div class="flex-grow">
                  <h4 class="text-heading-3 text-n-slate-12 mb-0.5">
                    {{ $t('INBOX_MGMT.ASSIGNMENT.DEFAULT_RULES_TITLE') }}
                  </h4>
                  <p class="text-body-main text-n-slate-11 mb-4">
                    {{ $t('INBOX_MGMT.ASSIGNMENT.DEFAULT_RULES_DESCRIPTION') }}
                  </p>

                  <ul class="space-y-2 mb-6">
                    <li class="flex items-center gap-2">
                      <span
                        class="w-1.5 h-1.5 rounded-full bg-n-slate-11 flex-shrink-0"
                      />
                      <span class="text-body-main text-n-slate-12">
                        {{ $t('INBOX_MGMT.ASSIGNMENT.DEFAULT_RULE_1') }}
                      </span>
                    </li>
                    <li class="flex items-center gap-2">
                      <span
                        class="w-1.5 h-1.5 rounded-full bg-n-slate-11 flex-shrink-0"
                      />
                      <span class="text-body-main text-n-slate-12">
                        {{ $t('INBOX_MGMT.ASSIGNMENT.DEFAULT_RULE_2') }}
                      </span>
                    </li>
                  </ul>

                  <div class="w-full h-px bg-n-weak my-4" />

                  <!-- Upgrade prompt when advanced_assignment is not enabled -->
                  <div v-if="!hasAdvancedAssignment">
                    <p class="text-body-main text-n-slate-11 mb-1">
                      {{ $t('INBOX_MGMT.ASSIGNMENT.UPGRADE_PROMPT') }}
                    </p>
                    <NextButton
                      :label="$t('INBOX_MGMT.ASSIGNMENT.UPGRADE_TO_BUSINESS')"
                      icon="i-lucide-arrow-right"
                      trailing-icon
                      link
                      @click="navigateToBilling"
                    />
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Old UI for non-assignment_v2 -->
          <template v-else-if="isEnterprise">
            <div class="p-4">
              <woot-input
                v-model="maxAssignmentLimit"
                type="number"
                :class="{ error: v$.maxAssignmentLimit.$error }"
                :error="maxAssignmentLimitErrors"
                :label="$t('INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT')"
                class="[&>input]:!mb-0"
                @blur="v$.maxAssignmentLimit.$touch"
              />

              <p class="mt-1.5 text-label-small text-n-slate-11">
                {{
                  $t('INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT_SUB_TEXT')
                }}
              </p>

              <div class="flex justify-end mt-4">
                <NextButton
                  :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
                  :disabled="v$.maxAssignmentLimit.$invalid"
                  @click="updateInbox"
                />
              </div>
            </div>
          </template>
        </template>
      </SettingsToggleSection>
    </SettingsAccordion>

    <!-- Unlinking the assignment policy changes how every conversation in the caixa is
         distributed — big-impact unlinks require typing the name to confirm. -->
    <woot-confirm-delete-modal
      v-if="showDeleteConfirmModal"
      v-model:show="showDeleteConfirmModal"
      :title="$t('INBOX_MGMT.ASSIGNMENT_POLICY.DELETE_CONFIRM_TITLE')"
      :message="$t('INBOX_MGMT.ASSIGNMENT_POLICY.DELETE_CONFIRM_MESSAGE')"
      :confirm-text="`${$t('INBOX_MGMT.ASSIGNMENT_POLICY.CONFIRM_DELETE')} ${assignmentPolicy?.name}`"
      :reject-text="$t('INBOX_MGMT.ASSIGNMENT_POLICY.CANCEL')"
      :confirm-value="assignmentPolicy?.name"
      :confirm-place-holder-text="
        $t('INBOX_MGMT.ASSIGNMENT_POLICY.DELETE_PLACE_HOLDER', {
          name: assignmentPolicy?.name,
        })
      "
      @on-confirm="deleteAssignmentPolicy"
      @on-close="cancelDeletePolicy"
    />
  </div>
</template>
